import axios from 'axios';
import { hämtaToken, glömToken } from '../context/AuthContext';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  // Ett anrop som inte svarar blir ett fel efter 30 sekunder, i stället för
  // att sidan står på "Laddar..." för evigt.
  timeout: 30000,
});

api.interceptors.request.use(async (config) => {
  const token = await hämtaToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Har token gått ut (till exempel när fliken legat i bakgrunden) svarar
// backend 401. Då hämtas en ny token och anropet görs om, en gång.
api.interceptors.response.use(undefined, async (fel) => {
  const config = fel?.config;
  if (fel?.response?.status === 401 && config && !config._nyttFörsök) {
    config._nyttFörsök = true;
    glömToken();
    return api(config);
  }
  return Promise.reject(fel);
});

export interface Kund {
  kund_id: string;
  namn: string;
  typ: string;
  segment: string;
  stad: string;
  matkoncept: string;
  ansvarig_säljare: string;
  aktiv: boolean | string;
  fortnox_kund_nr?: string;
  intern?: boolean;
}

export type AktivitetsTyp = 'möte' | 'epost' | 'samtal' | 'anteckning' | 'paminnelse';

export interface Mote {
  mote_id: string;
  kund_id: string | null;
  inkopare_id: string;
  säljare: string;
  datum: string;
  nästa_kontakt: string;
  notat: string;
  typ: AktivitetsTyp;
  granskad: boolean;
  avklarad: boolean;
}

export interface Vin {
  vin_id: string;
  namn: string;
  producent: string;
  land: string;
  region: string;
  typ: string;
  smakprofil_text: string;
  pris_kr: number;
  lager_flaskor: number;
}

export interface Inkopare {
  inkopare_id?: string;
  inköpare_id?: string;
  kund_id: string;
  namn: string;
  roll: string;
  smakprofil_text: string;
  föredrar_typ: string;
  prisnivå: string;
  telefon?: string;
  email?: string;
  extra_epost?: string[];
}

export const hämtaKunder = () => api.get<Kund[]>('/kunder/');
export const hämtaViner = () => api.get<Vin[]>('/viner/');
export const hämtaInkopare = () => api.get<Inkopare[]>('/inkopare/');
export const hämtaMoten = () => api.get<Mote[]>('/moten/');
export const hämtaMote = (mote_id: string) => api.get<Mote & { provningar: Provning[] }>(`/moten/${mote_id}`);
export const skapaKund = (data: Omit<Kund, 'kund_id'>) => api.post('/kunder/', data);
export const uppdateraKund = (kund_id: string, data: Omit<Kund, 'kund_id'>) => api.patch(`/kunder/${kund_id}`, data);
export interface Saljare {
  id: string;
  namn: string;
}

export const hämtaSäljare = () => api.get<Saljare[]>('/profiler/saljare');
export interface KöptArtikel {
  namn: string;
  antal: number;
  belopp: number;
  senast_köpt: string;
}

// En order i Fortnox som varken är fakturerad eller makulerad. Belopp exkl. moms.
export interface ÖppenOrder {
  order: string;
  datum: string | null;
  belopp: number;
  rader: { artikel_nr: string; namn: string; antal: number; belopp: number }[];
}

export interface Provning {
  provning_id?: string;
  mote_id: string;
  vin_namn: string;
  reaktion: string;
  kommentar: string;
  pris_accepterat: boolean | null;
  datum?: string;
  utfall?: ProvningsUtfall | null;
}
// Vad provningen ledde till, räknat från Fortnox-ordrar av samma vin hos kunden.
export type ProvningsUtfall =
  | { status: 'bestalld'; order: string; datum: string | null; a_pris: number | null; antal: number; ordrar: number }
  | { status: 'vantar'; dagar_kvar: number }
  | { status: 'inte_bestalld' };
export interface AnbudskalenderPost {
  id?: string;
  titel: string;
  kategori: 'sortimentsskifte' | 'offertstopp' | 'lanseringsprovstopp' | 'undantagsvecka';
  sortimentstyp?: 'fast' | 'tsv' | 'tst' | 'tsls' | 'tss' | 'bs' | null;
  datum_start: string;
  datum_slut?: string | null;
  uppdaterad_av?: string;
  created_at?: string;
}

export const hämtaAnbudskalender = () => api.get<AnbudskalenderPost[]>('/anbudskalender/');
export const skapaAnbudspost = (data: Omit<AnbudskalenderPost, 'id' | 'created_at'>) => api.post('/anbudskalender/', data);
export const taBortAnbudspost = (id: string) => api.delete(`/anbudskalender/${id}`);

export interface Kanalinkopare {
  id?: string;
  namn: string;
  ansvarsområde?: string;
  telefon?: string;
  email?: string;
}

export interface Kanalprovning {
  id?: string;
  aktivitet_id?: string;
  vin_namn: string;
  reaktion: number;
  pris_accepterat: boolean | null;
  kommentar?: string;
}

export interface Kanalaktivitet {
  id?: string;
  kanalinkopare_id: string;
  typ: 'möte' | 'provinlämning';
  datum: string;
  saljare_id?: string | null;
  notat?: string;
  provningar?: Kanalprovning[];
}

export const hämtaKanalinkopare = () => api.get<Kanalinkopare[]>('/kanalinkopare/');
export const skapaKanalinkopare = (data: Omit<Kanalinkopare, 'id'>) => api.post('/kanalinkopare/', data);
export const uppdateraKanalinkopare = (id: string, data: Omit<Kanalinkopare, 'id'>) => api.patch(`/kanalinkopare/${id}`, data);
export const taBortKanalinkopare = (id: string) => api.delete(`/kanalinkopare/${id}`);

export const hämtaKanalaktiviteter = () => api.get<Kanalaktivitet[]>('/kanalaktiviteter/');
export const skapaKanalaktivitet = (data: Omit<Kanalaktivitet, 'id'>) => api.post('/kanalaktiviteter/', data);
export const hämtaAktiviteterFörKanalinkopare = (kanalinkopare_id: string) =>
  api.get<Kanalaktivitet[]>(`/kanalaktiviteter/kanalinkopare/${kanalinkopare_id}/aktiviteter`);

export const hämtaKöptaArtiklar = (kund_nr: string) => api.get<{ hittad: boolean; artiklar: KöptArtikel[]; oppna_ordrar?: ÖppenOrder[] }>(`/fortnox/kopta-artiklar-per-kund?kund_nr=${encodeURIComponent(kund_nr)}`);
export interface Veckosammanstallning {
  antal_ordrar: number;
  ordervärde: number;
  antal_fakturor: number;
  fakturerat_värde: number;
}
export interface FortnoxHändelse {
  typ: 'order' | 'faktura';
  dokument_nr: string;
  datum: string;
  kund_namn: string;
  total: number;
  betald: boolean | null;
  var_referens: string | null;
  intern?: boolean;
}

export const hämtaFortnoxHändelser = (fran: string, till: string) =>
  api.get<FortnoxHändelse[]>(`/fortnox/handelser?fran=${fran}&till=${till}`);

export const hämtaVeckosammanstallning = (fran: string, till: string) =>
  api.get<Veckosammanstallning>(`/fortnox/veckosammanstallning?fran=${fran}&till=${till}`);
export const hämtaProvningarFörInkopare = (inkopare_id: string) => api.get<Provning[]>(`/inkopare/${inkopare_id}/provningar`);
export const skapaMote = (data: any) => api.post('/moten/', data);
export const uppdateraMote = (mote_id: string, data: any) => api.patch(`/moten/${mote_id}`, data);
export const taBortMote = (mote_id: string) => api.delete(`/moten/${mote_id}`);
export const skapaVin = (data: Omit<Vin, 'vin_id'>) => api.post('/viner/', data);
export const skapaInkopare = (data: Omit<Inkopare, 'inköpare_id'>) => api.post('/inkopare/', data);
export const uppdateraInkopare = (inkopare_id: string, data: Omit<Inkopare, 'inkopare_id' | 'inköpare_id'>) => api.patch(`/inkopare/${inkopare_id}`, data);
export const taBortInkopare = (inkopare_id: string) => api.delete(`/inkopare/${inkopare_id}`);

export default api;
export interface Kundforslag {
  id: number;
  fortnox_kund_nr: string;
  namn: string;
  stad?: string;
  foreslagen_kund_id?: string;
  foreslagen_kund?: { kund_id: string; namn: string; stad?: string; ansvarig_säljare?: string } | null;
}
export const hämtaKundförslag = () => api.get<Kundforslag[]>('/fortnox-synk/forslag');
export const kopplaKundförslag = (id: number) => api.post(`/fortnox-synk/forslag/${id}/koppla`);
export const skapaNyFrånKundförslag = (id: number) => api.post(`/fortnox-synk/forslag/${id}/ny`);

export interface LagerVin {
  artikel_nr: string;
  plats: string;
  namn: string;
  pris: number;
  saldo: number;
  lager: number;
  reserverade: number;
  nivå: string;
}
export interface Lagervarningar {
  uppdaterad: string;
  prisgräns: number;
  nivåer: { nivå: string; namn: string; min: number; max: number }[];
  viner: LagerVin[];
  antal_kritiska: number;
  slutsålda: number;
  utgaende: LagerUtgaende[];
}
export interface LagerUtgaende {
  artikel_nr: string;
  plats: string;
  namn: string;
  pris: number;
  saldo: number;
  markerad_av?: string;
  markerad?: string;
}
export const markeraUtgaende = (data: { artikel_nr: string; namn: string; saldo: number; markerad_av: string }) =>
  api.post('/lager/utgaende', data);
export const återställUtgaende = (artikel_nr: string) =>
  api.delete(`/lager/utgaende/${encodeURIComponent(artikel_nr)}`);
export const hämtaLagervarningar = (uppdatera = false) =>
  api.get<Lagervarningar>(`/lager/varningar${uppdatera ? '?uppdatera=true' : ''}`);

export interface Paminnelse {
  paminnelse_id: string;
  anvandare_id: string;
  text: string;
  kund_id: string | null;
  datum: string;
  klar: boolean;
  klar_datum?: string | null;
  kalla: string;
  aktivitet_id?: string | null;
}
export const hämtaPaminnelser = (inklusiveKlara = false) =>
  api.get<Paminnelse[]>(`/paminnelser/${inklusiveKlara ? '?inklusive_klara=true' : ''}`);
// Startsidan i Restaurang: översikten i fem rutor (3 nov 2026).
export interface RestaurangStartsida {
  person: string | null;
  admin: boolean;
  personer: string[];
  att_gora: {
    antal: number; forsenade: number; imorgon: number; kommande_14: number;
    poster: { typ: 'uppfoljning' | 'paminnelse'; datum: string; dagar_sen: number; kund_id: string | null; titel: string; detalj: string }[];
  };
  forsaljning: { manad: string; hittills: number; forra_samma_period: number; antal_fakturor: number; manader: { manad: string; netto: number }[] };
  provat: { antal: number; kunder: number; dagar: number;
    poster: { kund_id: string; kund: string; viner: string[]; antal_viner: number; dagar_kvar: number }[] };
  vilande: { antal: number; dagar: number; poster: { kund_id: string; kund: string; senast: string; dagar: number; ar_netto: number }[] };
  lager: { antal: number; kritiska: number; kritisk_max: number | null; prisgrans: number | null;
    poster: { artikel_nr: string; namn: string; saldo: number; pris: number }[] };
}
export const hämtaRestaurangStartsida = (person?: string) =>
  api.get<RestaurangStartsida>('/startsida/restaurang', { params: person ? { person } : {} });
export const hämtaPaminnelserFörKund = (kund_id: string) =>
  api.get<Paminnelse[]>(`/paminnelser/kund/${kund_id}`);
export const skapaPaminnelse = (data: { text: string; datum: string; kund_id?: string | null; kalla?: string }) =>
  api.post<Paminnelse>('/paminnelser/', data);
export const uppdateraPaminnelse = (
  paminnelse_id: string,
  data: { text?: string; datum?: string; kund_id?: string | null; klar?: boolean },
) => api.patch<Paminnelse>(`/paminnelser/${paminnelse_id}`, data);
export const taBortPaminnelse = (paminnelse_id: string) =>
  api.delete(`/paminnelser/${paminnelse_id}`);

export interface Artikel {
  artikel_nr: string;
  namn: string;
  plats?: string;
  pris: number;
}
export interface OklarMatchning {
  provning_id: string;
  vin_namn: string;
  producent?: string;
  pris?: number;
  artikel_nr?: string;
  artikel_namn?: string;
  matchning: string;
  provning_ids: string[];
  kunder: string[];
  senaste_datum?: string;
  vin_årgång?: string;
  kandidater: {
    artikel_nr: string; namn: string; pris: number; poäng: number;
    artikel_årgång?: string; ny_årgång: boolean;
  }[];
}
export const hämtaOklaraMatchningar = () =>
  api.get<OklarMatchning[]>('/artikelmatchning/oklara');
export const hämtaArtikelLista = () =>
  api.get<Artikel[]>('/artikelmatchning/artiklar');
export const bekräftaMatchning = (
  provning_id: string,
  data: { artikel_nr?: string; artikel_namn?: string; avvaktar?: boolean; ny_argang?: boolean; provning_ids?: string[] },
) => api.post(`/artikelmatchning/${provning_id}`, data);

export interface ÖvervägandeProvning {
  provning_id: string;
  datum?: string;
  kund_id?: string;
  kund?: string;
  inkopare?: string;
  säljare?: string;
  reaktion: string;
  kommentar?: string;
}
export interface Övervägande {
  vin_namn: string;
  producent: string;
  antal: number;
  gillade: number;
  senaste_datum?: string;
  producentläge: 'känd' | 'ny' | 'okänd';
  provningar: ÖvervägandeProvning[];
}
export const hämtaÖvervägande = () =>
  api.get<Övervägande[]>('/artikelmatchning/overvagande');
export const uppdateraÖvervägande = (
  data: { producent?: string; vin_namn?: string; provning_ids: string[] },
) => api.patch('/artikelmatchning/overvagande', data);

export interface DubblettKund {
  kund_id: string;
  namn: string;
  stad?: string | null;
  fortnox_kund_nr?: string | null;
  ansvarig_säljare?: string | null;
  aktiv?: boolean | null;
  inkopare: number;
  moten: number;
  paminnelser: number;
}
export interface DubblettPar {
  a: DubblettKund;
  b: DubblettKund;
  likhet: number;
  // Korten står på olika ort. Visas för sig, eftersom det oftare är olika ställen.
  olika_ort?: boolean;
}
export interface LiknandeKund {
  kund_id: string;
  namn: string;
  stad?: string | null;
  fortnox_kund_nr?: string | null;
  ansvarig_säljare?: string | null;
  likhet: number;
}
export const hämtaLiknandeKunder = (namn: string) =>
  api.get<LiknandeKund[]>(`/dubbletter/liknande?namn=${encodeURIComponent(namn)}`);
export interface SammanslagningResultat {
  borttagen_namn: string;
  inkopare_flyttade: number;
  inkopare_hopslagna: number;
  moten: number;
  paminnelser: number;
}
export const hämtaDubbletter = () =>
  api.get<DubblettPar[]>('/dubbletter');
export const slåIhopKunder = (behall: string, ta_bort: string) =>
  api.post<SammanslagningResultat>('/dubbletter/sla-ihop', { behall, ta_bort });
export const markeraInteSamma = (kund_a: string, kund_b: string) =>
  api.post('/dubbletter/inte-samma', { kund_a, kund_b });

// Demoläget. Statusvägen är öppen, så att även inloggningssidan kan fråga.
export const hämtaDemostatus = () =>
  api.get<{ demo: boolean; aterstalld?: string | null }>('/demo/status');
// Tömmer och fyller demodatabasen. Tar längre tid än ett vanligt anrop.
export const återställDemo = () =>
  api.post<{ status: string; tid?: string }>('/demo/aterstall', undefined, { timeout: 180000 });

export interface Händelse {
  tid: string;
  vem?: string | null;
  handelse: string;
  beskrivning: string;
}
export const hämtaKundHistorik = (kund_id: string) =>
  api.get<Händelse[]>(`/kunder/${kund_id}/historik`);

export interface LoggRad {
  id: number;
  tid: string;
  vem?: string | null;
  handelse: string;
  beskrivning: string;
  kund_id?: string | null;
  kund_namn?: string | null;
  modul?: string | null;
  producent_id?: string | null;
  producent_namn?: string | null;
  inkop_id?: string | null;
  // Fält som ändrats: [före, efter].
  andrat?: Record<string, [unknown, unknown]> | null;
  har_detaljer?: boolean;
}
export const hämtaHändelselogg = (dagar: number, modul = '') =>
  api.get<LoggRad[]>(`/handelselogg?dagar=${dagar}${modul ? `&modul=${modul}` : ''}`);
export const hämtaHändelse = (id: number) =>
  api.get<{ id: number; handelse: string; beskrivning: string; data: Record<string, unknown> }>(`/handelselogg/${id}`);


export interface Intagsfel {
  id: string;
  tid: string;
  avsandare: string | null;
  mottagare: string | null;
  orsak: string;
  text: string;
  kan_forsokas: boolean;
}
export const hämtaIntagsfel = () => api.get<Intagsfel[]>('/intagsfel');
// Tolkningen av ett långt mötesmejl kan ta upp mot en minut, så knappen väntar längre
// än vanliga anrop. Annars visas ett fel fast mejlet faktiskt läses in.
export const försökIgenIntagsfel = (id: string) =>
  api.post<{ status: string; mote_id?: string; felmeddelande?: string }>(`/intagsfel/${id}/forsok-igen`, undefined, { timeout: 120000 });
export const markeraIntagsfelHanterat = (id: string) => api.post(`/intagsfel/${id}/hanterad`);

export interface Systemstatus {
  nyckel: string;
  senast_ok: string | null;
  senaste_fel: string | null;
  larm_skickat?: string | null;
}
export const hämtaSystemstatus = () => api.get<Record<string, Systemstatus>>('/intagsfel/status');
// Adressen till Fortnox inloggning, där en admin godkänner Vinsales behörigheter.
export const hämtaFortnoxInloggning = () => api.get<{ url: string; behorigheter: string[] }>('/fortnox/inloggning');

// Efter varje lyckat sparande mot backend: meddela menyn, så att räknarna
// under Att åtgärda uppdateras direkt och inte väntar till nästa sidbyte.
api.interceptors.response.use(svar => {
  if (['post', 'put', 'patch', 'delete'].includes((svar.config.method || '').toLowerCase())) {
    window.dispatchEvent(new Event('vinsales:att-atgarda-andrad'));
  }
  return svar;
});
